import React, { useMemo } from "react"

import {
  CopyableId,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Tooltip,
  useCopyToClipboardFeedback,
} from "@coval/ui"
import { Check, Copy } from "lucide-react"

import { RowEventBoundary } from "@/components/tables/RowEventBoundary"
import {
  REVIEWER_STATUS,
  type ConversationsTableResult,
} from "@/types/tables/ConversationsTable"
import { cn } from "@/utils/cn"
import { formatFullTimestamp } from "@/utils/formatting/format-timestamp"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import type { ConversationsMetricTableRenderersById } from "../types/metricAdapters"
import {
  formatDateTimeWithTime,
  getTopLevelFields,
} from "../utils/tableCellUtils"
import { ConversationAudioCell } from "./ConversationAudioCell"
import { ConversationNotesInput } from "./ConversationNotesInput"
import { ConversationTagsCell } from "./ConversationTagsCell"

interface TableCellRendererProps {
  item: ConversationsTableResult
  columnKey: string
  metricRenderersById: ConversationsMetricTableRenderersById
  isMetricsLoading?: boolean
}

function MetadataFieldsPopover({
  fields,
}: {
  fields: Array<{ key: string; value: string }>
}) {
  const [copiedKey, setCopiedKey] = React.useState<string | null>(null)
  const { copied, copy } = useCopyToClipboardFeedback()

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="shrink-0 rounded-sm bg-surface-1 px-1.5 py-0.5 text-xs text-coval-text-muted hover:bg-surface-default-hover hover:text-coval-text"
          aria-label={`Show all ${fields.length} metadata fields`}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          +{fields.length - 1}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="max-h-80 w-96 max-w-[calc(100vw-2rem)] overflow-y-auto p-2"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <div className="px-2 pt-1 pb-2 text-xs font-semibold text-coval-text-muted">
          Metadata · click a value to copy
        </div>
        <div className="space-y-0.5">
          {fields.map(({ key, value }) => {
            const isCopied = copied && copiedKey === key
            return (
              <button
                key={key}
                type="button"
                className="grid w-full grid-cols-[minmax(0,7rem)_minmax(0,1fr)_1rem] items-start gap-2 rounded-sm px-2 py-1.5 text-left text-xs focus-visible:outline-none"
                aria-label={`Copy value for metadata key ${key}`}
                onClick={() => {
                  setCopiedKey(key)
                  void copy(value)
                }}
              >
                <span className="truncate font-medium text-coval-text-muted">
                  {key}
                </span>
                <span className="break-all text-coval-text">{value}</span>
                {isCopied ? (
                  <Check className="mt-0.5 size-3.5 text-coval-success-text" />
                ) : (
                  <Copy className="mt-0.5 size-3.5 text-coval-text-muted" />
                )}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Memoized cell renderer for the uploaded conversation table.
 * Renders different content based on columnKey.
 */
export const TableCellRenderer = React.memo<TableCellRendererProps>(
  ({ item, columnKey, metricRenderersById, isMetricsLoading = false }) => {
    const customerMetadataFields = useMemo(
      () => getTopLevelFields(item.customer_metadata),
      [item.customer_metadata],
    )

    if (columnKey.startsWith("metric_")) {
      const metricKey = columnKey.replace("metric_", "")

      // Show skeleton while metrics are loading
      if (isMetricsLoading) {
        return <Skeleton className="h-6 w-16 rounded-md" />
      }

      const metricRenderer = metricRenderersById[metricKey]

      // If no metric record found, show dash
      if (!metricRenderer) {
        return <span className="text-coval-text-muted">-</span>
      }

      // If no metric data exists for this row, show dash
      if (
        item.metrics[metricKey] === undefined &&
        !item.metric_error_statuses[metricKey]
      ) {
        return <span className="text-coval-text-muted">-</span>
      }

      return (
        <div className="flex w-full min-w-0 justify-center [&>div]:min-w-0 [&>div]:max-w-full [&>div>span.truncate]:!min-w-0 [&>div>span.truncate]:!max-w-full [&>div>span.truncate]:block [&>div>span.truncate]:leading-7">
          {metricRenderer.renderValueOnlyTableCell(
            item.metrics[metricKey],
            item.metric_error_statuses[metricKey],
            item.explanations[metricKey],
            item.metric_error_messages[metricKey],
          )}
        </div>
      )
    }

    switch (columnKey) {
      case "audio":
        return (
          <ConversationAudioCell
            simulationOutputId={item.simulation_output_id}
          />
        )
      case "simulation_output_id":
        if (!item.simulation_output_id) {
          return <span className="text-coval-text-muted">-</span>
        }

        return (
          <CopyableId
            id={item.simulation_output_id}
            label="Copy Simulation ID"
          />
        )
      case "created_at": {
        const displayTimestamp = item.created_at

        if (!displayTimestamp) {
          return <span className="text-coval-text-muted">-</span>
        }

        return (
          <div className="flex w-full min-w-0 items-center">
            <span
              className="block min-w-0 truncate whitespace-nowrap text-sm"
              title={formatFullTimestamp(displayTimestamp)}
            >
              {formatDateTimeWithTime(displayTimestamp)}
            </span>
          </div>
        )
      }
      case "occurred_at": {
        const displayTimestamp = item.occurred_at

        if (!displayTimestamp) {
          return <span className="text-coval-text-muted">-</span>
        }

        return (
          <div className="flex w-full min-w-0 items-center">
            <span
              className="block min-w-0 truncate whitespace-nowrap text-sm"
              title={formatFullTimestamp(displayTimestamp)}
            >
              {formatDateTimeWithTime(displayTimestamp)}
            </span>
          </div>
        )
      }
      case "tags":
        return (
          <RowEventBoundary>
            <ConversationTagsCell
              runId={item.run_id}
              initialTags={item.tags ?? undefined}
            />
          </RowEventBoundary>
        )
      case "cluster_description":
        if (!item.cluster_description) {
          return <span className="text-coval-text-muted">-</span>
        }

        return (
          <Tooltip content={item.cluster_description}>
            <span className="block min-w-0 truncate whitespace-nowrap text-sm">
              {item.cluster_description}
            </span>
          </Tooltip>
        )
      case "customer_metadata": {
        if (customerMetadataFields.length === 0) {
          return <span className="text-coval-text-muted">-</span>
        }

        const [firstField] = customerMetadataFields
        const remainingCount = customerMetadataFields.length - 1

        return (
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <span className="min-w-0 truncate whitespace-nowrap">
              <span className="text-coval-text-muted">{firstField.key}:</span>{" "}
              {firstField.value}
            </span>
            {remainingCount > 0 && (
              <MetadataFieldsPopover fields={customerMetadataFields} />
            )}
          </div>
        )
      }
      case "reviewer_assignees": {
        const assignees = item.reviewer_assignees ?? []
        if (assignees.length === 0) {
          return <span className="text-coval-text-muted">-</span>
        }

        const reviewerLabel = assignees.join(", ")
        const isReviewed = item.reviewer_status === REVIEWER_STATUS.COMPLETED
        const statusLabel = isReviewed ? "Reviewed" : "Assigned"

        return (
          <div className="flex min-w-0 items-center gap-2">
            <Tooltip content={reviewerLabel}>
              <span className="min-w-0 truncate text-sm">{reviewerLabel}</span>
            </Tooltip>
            <span
              className={cn(
                "shrink-0 rounded-sm px-1.5 py-0.5 text-xs font-medium",
                isReviewed
                  ? "bg-coval-success-surface text-coval-success-on-surface"
                  : "bg-surface-1 text-coval-text-muted",
              )}
            >
              {statusLabel}
            </span>
          </div>
        )
      }
      case "notes": {
        if (!item.simulation_output_id) {
          return <span className="text-coval-text-muted">-</span>
        }
        const notesItem = {
          simulation_output_id: item.simulation_output_id,
          notes: item.notes,
        }
        return (
          <div role="presentation" onClick={(e) => e.stopPropagation()}>
            <ConversationNotesInput
              item={
                notesItem as Parameters<
                  typeof ConversationNotesInput
                >[0]["item"]
              }
            />
          </div>
        )
      }
      case "actions":
        // For now, return empty actions cell - you can add action buttons here later
        return <span>-</span>
      default:
        // Render the raw value (or "-") for columns without a dedicated renderer
        return (
          <span>
            {stringifyUnknownValue(
              item[columnKey as keyof ConversationsTableResult] || "-",
            )}
          </span>
        )
    }
  },
)

TableCellRenderer.displayName = "TableCellRenderer"
